import { useRef, useState } from "react";
import { CoreButton } from "../button/button";
import { CoreInput } from "../input/input";
import { MarkdownPreview, safeMarkdownLink } from "./markdown_preview";

interface MarkdownEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  uploadImage: (file: File) => Promise<string>;
  busy?: boolean;
  invalid?: boolean;
}

export function MarkdownEditor({
  label,
  value,
  onChange,
  uploadImage,
  busy,
  invalid,
}: MarkdownEditorProps) {
  const [mode, setMode] = useState("formatted");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState("https://");
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const selection = useRef({ start: 0, end: 0 });
  const currentValue = useRef(value);
  currentValue.current = value;
  function rememberSelection() {
    selection.current = {
      start: textarea.current?.selectionStart || 0,
      end: textarea.current?.selectionEnd || 0,
    };
  }
  function insert(before: string, after = "", fallback = "") {
    const { start, end } = selection.current;
    const source = currentValue.current;
    const text = source.slice(start, end) || fallback;
    onChange(
      source.slice(0, start) + before + text + after + source.slice(end),
    );
    requestAnimationFrame(() => {
      textarea.current?.focus();
      textarea.current?.setSelectionRange(
        start + before.length,
        start + before.length + text.length,
      );
    });
  }
  async function upload(file?: File) {
    if (!file) return;
    setError("");
    if (
      file.size > 5 * 1024 * 1024 ||
      !["image/png", "image/jpeg", "image/webp"].includes(file.type)
    ) {
      setError("Выберите PNG, JPEG или WebP размером до 5 МБ");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      insert("![", `](${url})`, "Описание изображения");
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Не удалось загрузить изображение",
      );
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }
  const tools = [
    { title: "Жирный", before: "**", after: "**", text: "текст" },
    { title: "Курсив", before: "*", after: "*", text: "текст" },
    { title: "Заголовок", before: "\n\n## ", after: "\n", text: "Заголовок" },
    { title: "Список", before: "\n\n- ", after: "\n", text: "Пункт списка" },
    {
      title: "Нумерованный список",
      before: "\n\n1. ",
      after: "\n",
      text: "Пункт списка",
    },
    { title: "Формула", before: "$", after: "$", text: "x^2" },
    {
      title: "Формула отдельной строкой",
      before: "\n\n$$\n",
      after: "\n$$\n",
      text: "\\frac{a}{b}",
    },
  ];
  return (
    <div className="markdown-editor">
      <div
        className="markdown-modes"
        role="group"
        aria-label={`Режим: ${label}`}
      >
        <CoreButton
          filled={mode === "formatted"}
          aria-pressed={mode === "formatted"}
          onClick={() => setMode("formatted")}
        >
          С панелью
        </CoreButton>
        <CoreButton
          filled={mode === "source"}
          aria-pressed={mode === "source"}
          onClick={() => setMode("source")}
        >
          Markdown
        </CoreButton>
      </div>
      <fieldset disabled={busy || uploading} className="markdown-fields">
        {mode === "formatted" && (
          <div
            className="markdown-toolbar"
            role="group"
            aria-label={`Форматирование: ${label}`}
          >
            {tools.map((tool) => (
              <CoreButton
                key={tool.title}
                onMouseDown={rememberSelection}
                onFocus={rememberSelection}
                onClick={() => insert(tool.before, tool.after, tool.text)}
              >
                {tool.title}
              </CoreButton>
            ))}
            <CoreButton
              onMouseDown={rememberSelection}
              onFocus={rememberSelection}
              onClick={() => setLinkOpen(true)}
            >
              Ссылка
            </CoreButton>
            <CoreButton
              onMouseDown={rememberSelection}
              onFocus={rememberSelection}
              onClick={() => fileInput.current?.click()}
            >
              Загрузить изображение
            </CoreButton>
          </div>
        )}
        {linkOpen && (
          <div className="markdown-link">
            <CoreInput
              native
              label={`Адрес ссылки: ${label}`}
              value={link}
              onChange={setLink}
            />
            <CoreButton
              onClick={() => {
                if (!safeMarkdownLink(link)) {
                  setError(
                    "Используйте полный адрес http://, https:// или mailto:",
                  );
                  return;
                }
                insert("[", `](<${link}>)`, "текст ссылки");
                setLinkOpen(false);
                setError("");
              }}
            >
              Вставить ссылку
            </CoreButton>
            <CoreButton onClick={() => setLinkOpen(false)}>Отмена</CoreButton>
          </div>
        )}
        <div className="markdown-columns">
          <CoreInput
            native
            multiline
            textareaRef={textarea}
            label={label}
            rows={10}
            value={value}
            invalid={invalid}
            maxLength={100000}
            onChange={onChange}
          />
          <div className="markdown-live-preview">
            <p className="muted">Живой предпросмотр</p>
            <MarkdownPreview value={value} label={`Предпросмотр: ${label}`} />
          </div>
        </div>
        <input
          hidden
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          aria-label={`Изображение: ${label}`}
          onChange={(event) => upload(event.target.files?.[0])}
        />
      </fieldset>
      {uploading && <p role="status">Загрузка изображения…</p>}
      {error && (
        <p role="alert" className="field-error">
          {error}
        </p>
      )}
      <p className="muted">
        Markdown: **жирный**, *курсив*, $формула$, $$формула отдельной
        строкой$$. Изображения: PNG, JPEG, WebP до 5 МБ.
      </p>
    </div>
  );
}
